import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  DIFFICULTY_NAMES,
  GRAZE_SCORES,
  SEAL_RADIUS,
  type DifficultyId,
} from '../sim/difficulty';
import { DIFFICULTY_MUL } from '../game/meta';
import type { SecondaryDef } from '../sim/secondary';
import type { ShipDef, WeaponDef } from '../sim/defs';
import type { BombDef } from '../content/bombs';
import type { ConstraintDef } from '../content/constraints';

/* ═══════════════════════════════════════════════════════════════════════════
   L'écran de briefing : le choix de la difficulté, avant de lancer une partie.

   ⚠️ Il DIT ce que chaque palier change. C'est inhabituel — la plupart des jeux
   se contentent de « Facile / Normal / Difficile » et laissent deviner — et
   c'est la seule façon honnête de présenter des paliers qui modifient la nature
   des patterns et non un multiplicateur.

   Un joueur qui choisit « Vétéran » sans savoir que les éventails s'y mettent à
   tourner ne fait pas un choix éclairé : il découvrira au bout de dix minutes
   que ses repères ne fonctionnent plus, sans pouvoir attribuer la différence à
   son propre réglage. Trois lignes de texte suppriment ce malentendu.

   Le multiplicateur de crédits est affiché pour la même raison. Il existe, il
   change ce qu'on gagne, et le cacher ferait croire à un joueur prudent qu'il
   ne perd rien à rester en Recrue.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Ce que chaque palier change, en une phrase. Le texte vit près de l'écran
 *  qui l'affiche, mais les CHIFFRES viennent de `src/sim/difficulty.ts` : une
 *  description recopiée à la main finit toujours par mentir sur la valeur
 *  réellement appliquée. */
const RESUMES: readonly string[] = [
  "Les ennemis proches cessent de tirer d'assez loin pour offrir une sortie permanente. Le frôlement ne rapporte rien : mieux vaut apprendre à ne pas y toucher.",
  "La référence. Toutes les valeurs du jeu sont réglées ici, et c'est le seul palier où un score se compare sans qualificatif.",
  "Se coller à un ennemi ne le fait presque plus taire, et les éventails statiques TOURNENT d'une volée à l'autre : plus aucune place sûre ne se mémorise.",
];

export function BriefingScreen({
  initial,
  armes,
  armeInitiale,
  vaisseaux,
  vaisseauInitial,
  primaires,
  primaireInitiale,
  bombes,
  bombeInitiale,
  contraintes,
  contraintesInitiales,
  maxContraintes,
  best,
  onStart,
  onBack,
}: {
  initial: DifficultyId;
  /** Matériel débloqué. Chaque liste contient toujours au moins le contenu de
   *  série, y compris pour un joueur qui n'a rien acheté. */
  armes: readonly SecondaryDef[];
  armeInitiale: string;
  vaisseaux: readonly ShipDef[];
  vaisseauInitial: string;
  primaires: readonly WeaponDef[];
  primaireInitiale: string;
  bombes: readonly BombDef[];
  bombeInitiale: string;
  /** Contraintes volontaires disponibles, et le nombre qu'on peut cumuler. */
  contraintes: readonly ConstraintDef[];
  contraintesInitiales: readonly string[];
  maxContraintes: number;
  /** Meilleur score obtenu à ce palier, ou 0. */
  best: (d: DifficultyId) => number;
  onStart: (choix: {
    difficulty: DifficultyId;
    arme: string;
    vaisseau: string;
    primaire: string;
    bombe: string;
    contraintes: string[];
  }) => void;
  onBack: () => void;
}): ReactElement {
  const [choix, setChoix] = useState<DifficultyId>(initial);
  const [arme, setArme] = useState<string>(armeInitiale);
  const [vaisseau, setVaisseau] = useState<string>(vaisseauInitial);
  const [primaire, setPrimaire] = useState<string>(primaireInitiale);
  const [bombe, setBombe] = useState<string>(bombeInitiale);
  const [actives, setActives] = useState<string[]>([...contraintesInitiales]);

  /* ⚠️ Le cumul est borné ICI et re-borné dans `meta.ts`. La double vérification
     n'est pas de la redondance : cet écran peut être contourné (sauvegarde
     modifiée, version antérieure), et c'est le calcul des crédits qui doit
     rester incorruptible — pas l'interface. */
  const bascule = (id: string): void => {
    setActives((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < maxContraintes
          ? [...prev, id]
          : prev,
    );
  };
  const go = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    go.current?.focus();
  }, []);

  return (
    <div className="screen briefing">
      <h2>BRIEFING</h2>

      <div className="diff-list">
        {DIFFICULTY_NAMES.map((nom, i) => {
          const d = i as DifficultyId;
          const actif = d === choix;
          return (
            <button
              key={nom}
              className={`diff${actif ? ' actif' : ''}`}
              onClick={() => setChoix(d)}
              aria-pressed={actif}
            >
              <span className="diff-nom">{nom}</span>
              <span className="diff-resume">{RESUMES[i]}</span>
              <span className="diff-chiffres">
                Scellement {SEAL_RADIUS[i]} u · Frôlement {GRAZE_SCORES[i] ? 'compté' : 'neutre'} ·
                Crédits ×{(DIFFICULTY_MUL[i] ?? 1).toFixed(2)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Chaque famille ne s'affiche que s'il y a réellement un choix à faire.
          Une liste d'un seul élément n'est pas un choix : elle occupe l'écran,
          ajoute une décision inexistante, et laisse croire au joueur qu'il lui
          manque quelque chose. Chacune apparaît au premier déblocage acheté,
          ce qui rend l'achat immédiatement visible. */}
      <Famille titre="VAISSEAU" items={vaisseaux} actif={vaisseau} onPick={setVaisseau} />
      <Famille titre="ARME PRINCIPALE" items={primaires} actif={primaire} onPick={setPrimaire} />
      <Famille titre="BOMBE" items={bombes} actif={bombe} onPick={setBombe} />

      {contraintes.length > 0 && (
        <div className="arme-choix">
          <h3>CONTRAINTES VOLONTAIRES</h3>
          <p className="hint">
            Elles RETIRENT de la puissance contre davantage de crédits. {maxContraintes} au maximum
            ({actives.length} active{actives.length > 1 ? 's' : ''}).
          </p>
          <div className="arme-list">
            {contraintes.map((c) => (
              <button
                key={c.id}
                className={`arme${actives.includes(c.id) ? ' actif' : ''}`}
                onClick={() => bascule(c.id)}
                aria-pressed={actives.includes(c.id)}
              >
                <span className="arme-nom">{c.name}</span>
                <span className="arme-cd">crédits +{Math.round(c.creditBonus * 100)} %</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ⚠️ Le choix d'arme n'apparaît que s'il y a un choix à faire. Une liste
          d'un seul élément n'est pas un choix : elle occupe l'écran, ajoute une
          décision inexistante, et laisse croire au joueur qu'il lui manque
          quelque chose. Elle réapparaît dès le premier déblocage acheté au
          hangar, ce qui rend l'achat immédiatement visible. */}
      {armes.length > 1 && (
        <div className="arme-choix">
          <h3>ARME SECONDAIRE</h3>
          <p className="hint">Clic droit en jeu. Chacune a sa propre recharge.</p>
          <div className="arme-list">
            {armes.map((a) => (
              <button
                key={a.id}
                className={`arme${a.id === arme ? ' actif' : ''}`}
                onClick={() => setArme(a.id)}
                aria-pressed={a.id === arme}
              >
                <span className="arme-nom">{a.name}</span>
                <span className="arme-cd">recharge {a.cooldown.toFixed(1)} s</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {best(choix) > 0 && (
        // Le meilleur score est mémorisé PAR PALIER. Un record unique toutes
        // difficultés confondues pousserait à ne jouer qu'en Vétéran pour le
        // battre, ce qui vide les deux autres paliers de leur sens.
        <p>Meilleur score en {DIFFICULTY_NAMES[choix]} : {best(choix).toLocaleString('fr-FR')}</p>
      )}

      <div className="briefing-actions">
        <button ref={go} className="btn btn-primary" onClick={() =>
            onStart({
              difficulty: choix,
              arme,
              vaisseau,
              primaire,
              bombe,
              contraintes: actives,
            })
          }>
          LANCER
        </button>
        <button className="btn" onClick={onBack}>
          RETOUR
        </button>
      </div>
    </div>
  );
}

/** Une famille d'équipement à choix unique.
 *
 *  Générique volontairement : vaisseaux, armes primaires et bombes posent
 *  exactement la même question — « lequel emportes-tu ? » — et trois blocs
 *  recopiés auraient divergé à la première retouche. Les contraintes, elles,
 *  ont leur propre bloc : ce sont des cases à cocher multiples, pas un choix. */
function Famille<T extends { id: string; name: string }>({
  titre,
  items,
  actif,
  onPick,
}: {
  titre: string;
  items: readonly T[];
  actif: string;
  onPick: (id: string) => void;
}): ReactElement | null {
  if (items.length < 2) return null;
  return (
    <div className="arme-choix">
      <h3>{titre}</h3>
      <div className="arme-list">
        {items.map((it) => (
          <button
            key={it.id}
            className={`arme${it.id === actif ? ' actif' : ''}`}
            onClick={() => onPick(it.id)}
            aria-pressed={it.id === actif}
          >
            <span className="arme-nom">{it.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
